import React, { useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt,
  faUser,
  faEnvelope,
  faPhone,
  faCalendar,
  faMapMarkerAlt,
  faClock,
  faExclamationTriangle,
  faFileAlt,
  faCar,
  faTools,
  faExternalLinkAlt,
  faReply,
  faCheckCircle,
  faHourglass,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import {
  GET_DAMAGE_CLAIM_FOR_ADMIN,
  UPDATE_DAMAGE_CLAIM_STATUS,
  ADD_DAMAGE_CLAIM_NOTES,
} from '../../services/damageClaimService/adminQueries';
import { DamageClaimStatus, DamageType } from '../../../__generated__/graphql';
import { URLS } from '../../../constants/urls';
import { toPublicS3ObjectUrl } from '../../utils/s3PublicUrl';
import { Button } from '../../components/button';
import { Modal } from '../../components/modal';
import NumericInput from '../../components/NumericInput';

import {
  Badge,
  BadgeTone,
  LoadingBlock,
  PageContainer as UiPageContainer,
  PageHeader,
} from '../../components/ui';

const PageContainer = styled(UiPageContainer).attrs({ width: 'lg', canvas: true })``;

const Section = styled.div`
  ${tw`p-6 mb-6`}
  background-color: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-card);
`;

const SectionTitle = styled.h2`
  ${tw`text-base font-semibold mb-4 flex items-center gap-2`}
  color: var(--color-text-primary);
`;

const claimStatusTone = (status: DamageClaimStatus): BadgeTone => {
  switch (status) {
    case DamageClaimStatus.Pending:
      return 'warning';
    case DamageClaimStatus.UnderReview:
      return 'info';
    case DamageClaimStatus.Approved:
    case DamageClaimStatus.PartiallyApproved:
      return 'success';
    case DamageClaimStatus.Rejected:
      return 'error';
    default:
      return 'neutral';
  }
};

const InfoGrid = styled.div`
  ${tw`grid grid-cols-1 md:grid-cols-2 gap-4`}
`;

const InfoRow = styled.div`
  ${tw`flex justify-between py-2 border-b border-gray-100`}
`;

const InfoLabel = styled.span`
  ${tw`text-gray-500 text-sm`}
`;

const InfoValue = styled.span`
  ${tw`text-gray-900 font-medium text-sm`}
`;

const ContactCard = styled.div`
  ${tw`p-4 rounded-lg border border-gray-200`}
`;

const ContactName = styled.div`
  ${tw`font-semibold text-gray-900 mb-2`}
`;

const ContactRow = styled.div`
  ${tw`flex items-center gap-2 text-sm text-gray-600 mb-1`}
`;

const PhotoGrid = styled.div`
  ${tw`grid grid-cols-2 md:grid-cols-4 gap-4`}
`;

const PhotoWrapper = styled.div`
  ${tw`relative aspect-square rounded-lg overflow-hidden border border-gray-200`}
`;

const Photo = styled.img`
  ${tw`w-full h-full object-cover`}
`;

const PhotoLabel = styled.div`
  ${tw`absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs py-1 px-2`}
`;

const DamagedPartsList = styled.div`
  ${tw`flex flex-wrap gap-2`}
`;

const DamagedPartTag = styled.span`
  ${tw`px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm`}
`;

const AssessmentGrid = styled.div`
  ${tw`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4`}
`;

const AssessmentItem = styled.div`
  ${tw`p-3 rounded-lg border border-gray-100`}
`;

const AssessmentLabel = styled.div`
  ${tw`text-xs text-gray-500 mb-1`}
`;

const AssessmentValue = styled.div<{ value?: string }>`
  ${tw`font-medium`}
  ${props => {
    if (props.value === 'YES') return tw`text-red-600`;
    if (props.value === 'NO') return tw`text-green-600`;
    return tw`text-gray-600`;
  }}
`;

const ActionButtonsContainer = styled.div`
  ${tw`flex flex-wrap gap-4 mt-6`}
`;

const BookingLink = styled(Link)`
  ${tw`text-pink-600 hover:text-pink-700 flex items-center gap-1`}
`;

const AdminNotesSection = styled.div`
  ${tw`mt-4 p-4 bg-gray-50 rounded-lg`}
`;

const AdminNotesTextarea = styled.textarea`
  ${tw`w-full p-3 border border-gray-300 rounded-lg text-sm min-h-[100px]`}
`;

const ModalContent = styled.div`
  ${tw`flex flex-col gap-4`}
`;

const ModalInput = styled.input`
  ${tw`w-full p-3 border border-gray-300 rounded-lg`}
`;

const ModalTextarea = styled.textarea`
  ${tw`w-full p-3 border border-gray-300 rounded-lg min-h-[100px]`}
`;

const ModalLabel = styled.label`
  ${tw`text-sm font-medium text-gray-700 mb-1`}
`;

const CarInfoSection = styled.div`
  ${tw`flex items-center gap-4`}
`;

const CarThumbnail = styled.img`
  ${tw`w-20 h-16 object-cover rounded-lg`}
`;

// Response Section Styles
const ResponseSection = styled(Section)`
  ${tw`border-2 border-blue-200 bg-blue-50`}
`;

const ResponseDeadlineBar = styled.div<{ isExpired: boolean }>`
  ${tw`flex items-center gap-2 px-4 py-3 rounded-lg mb-4`}
  ${props => (props.isExpired ? tw`bg-red-100 text-red-700` : tw`bg-yellow-100 text-yellow-700`)}
`;

const ExistingResponse = styled.div`
  ${tw`bg-white rounded-lg p-4 border border-gray-200`}
`;

const ResponseHeader = styled.div`
  ${tw`flex items-center gap-2 mb-3`}
`;

const ResponderName = styled.span`
  ${tw`font-medium text-gray-900`}
`;

const ResponseDate = styled.span`
  ${tw`text-sm text-gray-500`}
`;

const ResponseContent = styled.p`
  ${tw`text-gray-700 whitespace-pre-wrap`}
`;

const ResponseStatusBadge = styled.span<{ reviewed: boolean }>`
  ${tw`px-3 py-1 rounded-full text-sm font-medium ml-auto`}
  ${props => (props.reviewed ? tw`bg-green-100 text-green-800` : tw`bg-yellow-100 text-yellow-800`)}
`;

const NoResponseYet = styled.div`
  ${tw`flex items-center gap-2 text-gray-500 italic p-4 bg-white rounded-lg border border-gray-200`}
`;

const getStatusLabel = (status: DamageClaimStatus): string => {
  switch (status) {
    case DamageClaimStatus.Pending:
      return 'Pending';
    case DamageClaimStatus.UnderReview:
      return 'Under Review';
    case DamageClaimStatus.Approved:
      return 'Approved';
    case DamageClaimStatus.PartiallyApproved:
      return 'Partially Approved';
    case DamageClaimStatus.Rejected:
      return 'Rejected';
    case DamageClaimStatus.Resolved:
      return 'Resolved';
    case DamageClaimStatus.Withdrawn:
      return 'Withdrawn';
    default:
      return status;
  }
};

const formatDate = (dateValue: string | number | Date | null | undefined): string => {
  if (!dateValue) return 'N/A';
  let date: Date;
  if (typeof dateValue === 'string') {
    const parsed = parseInt(dateValue);
    if (!isNaN(parsed) && String(parsed) === dateValue) {
      date = new Date(parsed);
    } else {
      date = new Date(dateValue);
    }
  } else {
    date = new Date(dateValue);
  }

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatPartName = (part: string): string => {
  return part
    .split('_')
    .map(word => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
};

const getTimeRemaining = (
  deadline: string | number | Date | null | undefined,
): { text: string; isExpired: boolean } | null => {
  if (!deadline) return null;
  const deadlineDate = new Date(deadline);
  const now = new Date();
  const diff = deadlineDate.getTime() - now.getTime();

  if (diff <= 0) {
    return { text: 'Response deadline has passed', isExpired: true };
  }

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return { text: `${days} day${days > 1 ? 's' : ''} remaining to respond`, isExpired: false };
  }

  if (hours > 0) {
    return { text: `${hours}h ${minutes}m remaining to respond`, isExpired: false };
  }

  return { text: `${minutes} minutes remaining to respond`, isExpired: false };
};

const AdminClaimDetailPage: React.FC = () => {
  const { claimId } = useParams();

  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [approvedAmount, setApprovedAmount] = useState<string>('');
  const [hostClaimAmount, setHostClaimAmount] = useState<string>('');
  const [guestShouldPay, setGuestShouldPay] = useState<string>('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [newNote, setNewNote] = useState('');

  const { data, loading, error, refetch } = useQuery(GET_DAMAGE_CLAIM_FOR_ADMIN, {
    variables: { id: claimId },
    skip: !claimId,
    fetchPolicy: 'network-only',
  });

  const [updateClaimStatus, { loading: updating }] = useMutation(UPDATE_DAMAGE_CLAIM_STATUS, {
    onCompleted: () => {
      toast.success('Claim status updated successfully');
      setIsApproveModalOpen(false);
      setIsRejectModalOpen(false);
      refetch();
    },
    onError: err => {
      toast.error(`Failed to update status: ${err.message}`);
    },
  });

  const [addNotes, { loading: addingNotes }] = useMutation(ADD_DAMAGE_CLAIM_NOTES, {
    onCompleted: () => {
      toast.success('Notes added successfully');
      setNewNote('');
      refetch();
    },
    onError: err => {
      toast.error(`Failed to add notes: ${err.message}`);
    },
  });

  const claim = data?.damageClaim;

  const handleStartReview = () => {
    updateClaimStatus({
      variables: {
        input: {
          claimId,
          status: DamageClaimStatus.UnderReview,
        },
      },
    });
  };

  const handleApprove = (isPartial: boolean) => {
    const amount = parseFloat(approvedAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid approved amount');
      return;
    }
    const hostAmount = hostClaimAmount ? parseFloat(hostClaimAmount) : undefined;
    const guestAmount = guestShouldPay ? parseFloat(guestShouldPay) : undefined;

    if (hostClaimAmount && (isNaN(hostAmount!) || hostAmount! < 0)) {
      toast.error('Please enter a valid host claim amount');
      return;
    }
    if (guestShouldPay && (isNaN(guestAmount!) || guestAmount! < 0)) {
      toast.error('Please enter a valid guest payment amount');
      return;
    }

    updateClaimStatus({
      variables: {
        input: {
          claimId,
          status: isPartial ? DamageClaimStatus.PartiallyApproved : DamageClaimStatus.Approved,
          approvedAmount: amount,
          hostClaimAmount: hostAmount,
          guestShouldPay: guestAmount,
          adminNotes,
        },
      },
    });
  };

  const handleReject = () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    updateClaimStatus({
      variables: {
        input: {
          claimId,
          status: DamageClaimStatus.Rejected,
          rejectionReason,
          adminNotes,
        },
      },
    });
  };

  const handleResolve = () => {
    updateClaimStatus({
      variables: {
        input: {
          claimId,
          status: DamageClaimStatus.Resolved,
        },
      },
    });
  };

  const handleAddNote = () => {
    if (!newNote.trim()) {
      toast.error('Please enter a note');
      return;
    }
    addNotes({
      variables: {
        input: {
          claimId,
          notes: newNote.trim(),
        },
      },
    });
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingBlock label="Loading claim" minHeight="320px" />
      </PageContainer>
    );
  }

  if (error || !claim) {
    return (
      <PageContainer>
        <PageHeader
          eyebrow="Admin"
          backTo={{ to: '/admin/damage-claims', label: 'All claims' }}
          title="Claim not found"
          description={`Error loading claim: ${error?.message || 'Claim not found'}`}
        />
      </PageContainer>
    );
  }

  const canApproveOrReject =
    claim.status === DamageClaimStatus.Pending || claim.status === DamageClaimStatus.UnderReview;
  const canResolve =
    claim.status === DamageClaimStatus.Approved ||
    claim.status === DamageClaimStatus.PartiallyApproved;

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Admin"
        backTo={{ to: '/admin/damage-claims', label: 'All claims' }}
        title={
          <>
            Claim #{claim.claimNum}{' '}
            <Badge tone={claimStatusTone(claim.status)} dot size="md">
              {getStatusLabel(claim.status)}
            </Badge>
          </>
        }
      />

      {/* Status Action Buttons */}
      {(canApproveOrReject || canResolve) && (
        <Section>
          <SectionTitle>
            <FontAwesomeIcon icon={faTools} />
            Actions
          </SectionTitle>
          <ActionButtonsContainer>
            {claim.status === DamageClaimStatus.Pending && (
              <Button
                text="Start Review"
                onClick={handleStartReview}
                isLoading={updating}
                buttonWidth="150px"
              />
            )}
            {canApproveOrReject && (
              <>
                <Button
                  text="Approve"
                  onClick={() => {
                    setApprovedAmount(claim.claimAmount?.toString() || '');
                    setHostClaimAmount(claim.hostClaimAmount?.toString() || '');
                    setGuestShouldPay(claim.guestShouldPay?.toString() || '');
                    setIsApproveModalOpen(true);
                  }}
                  isLoading={updating}
                  buttonWidth="150px"
                />
                <Button
                  text="Reject"
                  theme="outlined"
                  onClick={() => setIsRejectModalOpen(true)}
                  isLoading={updating}
                  buttonWidth="150px"
                />
              </>
            )}
            {canResolve && (
              <Button
                text="Mark as Resolved"
                onClick={handleResolve}
                isLoading={updating}
                buttonWidth="180px"
              />
            )}
          </ActionButtonsContainer>
        </Section>
      )}

      {/* Claim Overview */}
      <Section>
        <SectionTitle>
          <FontAwesomeIcon icon={faFileAlt} />
          Claim Overview
        </SectionTitle>
        <InfoGrid>
          <div>
            <InfoRow>
              <InfoLabel>Claim ID</InfoLabel>
              <InfoValue>{claim.id}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Claim Number</InfoLabel>
              <InfoValue>#{claim.claimNum}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Damage Type</InfoLabel>
              <InfoValue>
                {claim.damageType === DamageType.Physical ? 'Physical Damage' : 'Mechanical Issue'}
              </InfoValue>
            </InfoRow>
            {claim.physicalDamageSubcategory && (
              <InfoRow>
                <InfoLabel>Subcategory</InfoLabel>
                <InfoValue>{formatPartName(claim.physicalDamageSubcategory)}</InfoValue>
              </InfoRow>
            )}
            <InfoRow>
              <InfoLabel>Submitted</InfoLabel>
              <InfoValue>{formatDate(claim.createdAt)}</InfoValue>
            </InfoRow>
          </div>
          <div>
            <InfoRow>
              <InfoLabel>Repair Estimate</InfoLabel>
              <InfoValue>{claim.repairEstimateRange?.replace(/_/g, ' ') || 'N/A'}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Claim Amount</InfoLabel>
              <InfoValue>${claim.claimAmount?.toFixed(2) || 'N/A'}</InfoValue>
            </InfoRow>
            {claim.approvedAmount != null && (
              <InfoRow>
                <InfoLabel>Approved Amount</InfoLabel>
                <InfoValue className="text-green-600">${claim.approvedAmount.toFixed(2)}</InfoValue>
              </InfoRow>
            )}
            {claim.hostClaimAmount != null && (
              <InfoRow>
                <InfoLabel>Host Claim Amount</InfoLabel>
                <InfoValue className="text-blue-600">${claim.hostClaimAmount.toFixed(2)}</InfoValue>
              </InfoRow>
            )}
            {claim.guestShouldPay != null && (
              <InfoRow>
                <InfoLabel>Guest Should Pay</InfoLabel>
                <InfoValue className="text-orange-600">
                  ${claim.guestShouldPay.toFixed(2)}
                </InfoValue>
              </InfoRow>
            )}
            {claim.rejectionReason && (
              <InfoRow>
                <InfoLabel>Rejection Reason</InfoLabel>
                <InfoValue className="text-red-600">{claim.rejectionReason}</InfoValue>
              </InfoRow>
            )}
            {claim.reviewedAt && (
              <InfoRow>
                <InfoLabel>Reviewed At</InfoLabel>
                <InfoValue>{formatDate(claim.reviewedAt)}</InfoValue>
              </InfoRow>
            )}
          </div>
        </InfoGrid>
      </Section>

      {/* Booking & Car Info */}
      <Section>
        <SectionTitle>
          <FontAwesomeIcon icon={faCar} />
          Booking & Car
        </SectionTitle>
        {claim.booking && (
          <>
            <CarInfoSection>
              {claim.booking.car?.thumbnailUrl && (
                <CarThumbnail
                  src={`${URLS.S3_BUCKET.CAR_THUMBNAILS}/${claim.booking.car.thumbnailUrl}`}
                  alt="Car"
                />
              )}
              <div>
                <InfoValue>
                  {claim.booking.car?.year} {claim.booking.car?.make} {claim.booking.car?.model}
                </InfoValue>
                <InfoLabel>
                  Trip: {formatDate(claim.booking.startAt)} - {formatDate(claim.booking.endAt)}
                </InfoLabel>
              </div>
            </CarInfoSection>
            <InfoRow className="mt-4">
              <InfoLabel>Booking ID</InfoLabel>
              <BookingLink to={`/cars/host/booking/${claim.booking.id}`}>
                {claim.booking.id.slice(0, 8)}...
                <FontAwesomeIcon icon={faExternalLinkAlt} className="text-xs" />
              </BookingLink>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Security Deposit</InfoLabel>
              <InfoValue>
                ${claim.booking.securityDepositAmount?.toFixed(2) || '0.00'} (
                {claim.booking.securityDepositStatus || 'N/A'})
              </InfoValue>
            </InfoRow>
          </>
        )}
      </Section>

      {/* Protection Plan Snapshot */}
      {claim.protectionPlanSnapshot && (
        <Section>
          <SectionTitle>
            <FontAwesomeIcon icon={faShieldAlt} className="text-green-500" />
            Guest Protection Plan (at time of booking)
          </SectionTitle>
          <InfoGrid>
            <InfoRow>
              <InfoLabel>Plan Name</InfoLabel>
              <InfoValue>{claim.protectionPlanSnapshot.planName}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Plan Type</InfoLabel>
              <InfoValue>{claim.protectionPlanSnapshot.planType}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Daily Price</InfoLabel>
              <InfoValue>${claim.protectionPlanSnapshot.dailyPrice.toFixed(2)}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Out of Pocket (Deductible)</InfoLabel>
              <InfoValue className="text-orange-600 font-semibold">
                ${claim.protectionPlanSnapshot.outOfPocket.toFixed(2)}
              </InfoValue>
            </InfoRow>
            {claim.protectionPlanSnapshot.description && (
              <div className="col-span-2">
                <InfoLabel>Description</InfoLabel>
                <p className="text-sm text-gray-700 mt-1">
                  {claim.protectionPlanSnapshot.description}
                </p>
              </div>
            )}
          </InfoGrid>
        </Section>
      )}

      {/* Parties Involved */}
      <Section>
        <SectionTitle>
          <FontAwesomeIcon icon={faUser} />
          Parties Involved
        </SectionTitle>
        <InfoGrid>
          <ContactCard>
            <ContactName>Host</ContactName>
            <ContactRow>
              <FontAwesomeIcon icon={faUser} className="text-gray-400" />
              {claim.booking?.host?.displayName || claim.booking?.host?.firstName || 'N/A'}
            </ContactRow>
            {claim.booking?.host?.email && (
              <ContactRow>
                <FontAwesomeIcon icon={faEnvelope} className="text-gray-400" />
                <a href={`mailto:${claim.booking.host.email}`} className="text-pink-600">
                  {claim.booking.host.email}
                </a>
              </ContactRow>
            )}
          </ContactCard>
          <ContactCard>
            <ContactName>Guest</ContactName>
            <ContactRow>
              <FontAwesomeIcon icon={faUser} className="text-gray-400" />
              {claim.booking?.guest?.displayName || claim.booking?.guest?.firstName || 'N/A'}
            </ContactRow>
            {claim.booking?.guest?.email && (
              <ContactRow>
                <FontAwesomeIcon icon={faEnvelope} className="text-gray-400" />
                <a href={`mailto:${claim.booking.guest.email}`} className="text-pink-600">
                  {claim.booking.guest.email}
                </a>
              </ContactRow>
            )}
            {claim.booking?.guest?.phoneNumber && (
              <ContactRow>
                <FontAwesomeIcon icon={faPhone} className="text-gray-400" />
                <a href={`tel:${claim.booking.guest.phoneNumber}`} className="text-pink-600">
                  {claim.booking.guest.phoneNumber}
                </a>
              </ContactRow>
            )}
          </ContactCard>
        </InfoGrid>
        <div className="mt-4">
          <InfoLabel>Reporter: </InfoLabel>
          <InfoValue>
            {claim.reporter?.displayName || claim.reporter?.firstName || 'N/A'} (
            {claim.reporterId === claim.hostId ? 'Host' : 'Guest'})
          </InfoValue>
        </div>
      </Section>

      {/* Photos */}
      {(claim.beforePhotos?.length > 0 || claim.afterPhotos?.length > 0) && (
        <Section>
          <SectionTitle>
            <FontAwesomeIcon icon={faFileAlt} />
            Photos & Documents
          </SectionTitle>
          <PhotoGrid>
            {claim.beforePhotos?.map((photo: string, idx: number) => (
              <PhotoWrapper key={`before-${idx}`}>
                <Photo src={toPublicS3ObjectUrl(photo)} alt={`Before ${idx + 1}`} />
                <PhotoLabel>Before #{idx + 1}</PhotoLabel>
              </PhotoWrapper>
            ))}
            {claim.afterPhotos?.map((photo: string, idx: number) => (
              <PhotoWrapper key={`after-${idx}`}>
                <Photo src={toPublicS3ObjectUrl(photo)} alt={`After ${idx + 1}`} />
                <PhotoLabel>After #{idx + 1}</PhotoLabel>
              </PhotoWrapper>
            ))}
          </PhotoGrid>
        </Section>
      )}

      {/* Damaged Parts */}
      {claim.damagedParts?.length > 0 && (
        <Section>
          <SectionTitle>Damaged Parts</SectionTitle>
          <DamagedPartsList>
            {claim.damagedParts.map((part: string) => (
              <DamagedPartTag key={part}>{formatPartName(part)}</DamagedPartTag>
            ))}
          </DamagedPartsList>
          {claim.damagedPartsOtherDescription && (
            <p className="mt-3 text-sm text-gray-600">
              Other: {claim.damagedPartsOtherDescription}
            </p>
          )}
        </Section>
      )}

      {/* Assessment Questions */}
      <Section>
        <SectionTitle>
          <FontAwesomeIcon icon={faExclamationTriangle} />
          Assessment Details
        </SectionTitle>
        <AssessmentGrid>
          {claim.policeReportFiled && (
            <AssessmentItem>
              <AssessmentLabel>Police Report Filed</AssessmentLabel>
              <AssessmentValue value={claim.policeReportFiled}>
                {claim.policeReportFiled}
              </AssessmentValue>
            </AssessmentItem>
          )}
          {claim.waterInsideCar && (
            <AssessmentItem>
              <AssessmentLabel>Water Inside Car</AssessmentLabel>
              <AssessmentValue value={claim.waterInsideCar}>{claim.waterInsideCar}</AssessmentValue>
            </AssessmentItem>
          )}
          {claim.isCarDriveable && (
            <AssessmentItem>
              <AssessmentLabel>Car Driveable</AssessmentLabel>
              <AssessmentValue value={claim.isCarDriveable}>{claim.isCarDriveable}</AssessmentValue>
            </AssessmentItem>
          )}
          {claim.didVehicleLoseFluids && (
            <AssessmentItem>
              <AssessmentLabel>Vehicle Lost Fluids</AssessmentLabel>
              <AssessmentValue value={claim.didVehicleLoseFluids}>
                {claim.didVehicleLoseFluids}
              </AssessmentValue>
            </AssessmentItem>
          )}
          {claim.didAirbagsDeploy && (
            <AssessmentItem>
              <AssessmentLabel>Airbags Deployed</AssessmentLabel>
              <AssessmentValue value={claim.didAirbagsDeploy}>
                {claim.didAirbagsDeploy}
              </AssessmentValue>
            </AssessmentItem>
          )}
          {claim.anyoneInjured && (
            <AssessmentItem>
              <AssessmentLabel>Anyone Injured</AssessmentLabel>
              <AssessmentValue value={claim.anyoneInjured}>{claim.anyoneInjured}</AssessmentValue>
            </AssessmentItem>
          )}
          {claim.anotherPersonInvolved && (
            <AssessmentItem>
              <AssessmentLabel>Another Person Involved</AssessmentLabel>
              <AssessmentValue value={claim.anotherPersonInvolved}>
                {claim.anotherPersonInvolved}
              </AssessmentValue>
            </AssessmentItem>
          )}
          {claim.whoWasDriving && (
            <AssessmentItem>
              <AssessmentLabel>Who Was Driving</AssessmentLabel>
              <AssessmentValue>{formatPartName(claim.whoWasDriving)}</AssessmentValue>
            </AssessmentItem>
          )}
        </AssessmentGrid>

        {claim.incidentOccurredAt && (
          <div className="mt-4">
            <InfoRow>
              <InfoLabel>
                <FontAwesomeIcon icon={faCalendar} className="mr-2" />
                Incident Date/Time
              </InfoLabel>
              <InfoValue>{formatDate(claim.incidentOccurredAt)}</InfoValue>
            </InfoRow>
          </div>
        )}

        {claim.incidentLocation && (
          <div>
            <InfoRow>
              <InfoLabel>
                <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2" />
                Incident Location
              </InfoLabel>
              <InfoValue>
                {[
                  claim.incidentLocation.streetAddress,
                  claim.incidentLocation.city,
                  claim.incidentLocation.postalCode,
                  claim.incidentLocation.country,
                ]
                  .filter(Boolean)
                  .join(', ')}
              </InfoValue>
            </InfoRow>
          </div>
        )}
      </Section>

      {/* Mechanical Issues */}
      {claim.mechanicalIssues?.length > 0 && (
        <Section>
          <SectionTitle>Mechanical Issues</SectionTitle>
          <DamagedPartsList>
            {claim.mechanicalIssues.map((issue: string) => (
              <DamagedPartTag key={issue}>{formatPartName(issue)}</DamagedPartTag>
            ))}
          </DamagedPartsList>
          {claim.mechanicalIssueOtherDescription && (
            <p className="mt-3 text-sm text-gray-600">
              Other: {claim.mechanicalIssueOtherDescription}
            </p>
          )}
        </Section>
      )}

      {/* Damage Description */}
      {claim.damageDescription && (
        <Section>
          <SectionTitle>Damage Description</SectionTitle>
          <p className="text-gray-700 whitespace-pre-wrap">{claim.damageDescription}</p>
        </Section>
      )}

      {/* Repair Shop Info */}
      {claim.repairShopInfo && (
        <Section>
          <SectionTitle>Repair Shop Information</SectionTitle>
          <InfoGrid>
            <InfoRow>
              <InfoLabel>Shop Name</InfoLabel>
              <InfoValue>{claim.repairShopInfo.shopName || 'N/A'}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Address</InfoLabel>
              <InfoValue>{claim.repairShopInfo.address || 'N/A'}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Contact</InfoLabel>
              <InfoValue>{claim.repairShopInfo.contactName || 'N/A'}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Phone</InfoLabel>
              <InfoValue>{claim.repairShopInfo.phoneNumber || 'N/A'}</InfoValue>
            </InfoRow>
          </InfoGrid>
        </Section>
      )}

      {/* Additional Info */}
      {claim.additionalInfo && (
        <Section>
          <SectionTitle>Additional Information</SectionTitle>
          <p className="text-gray-700 whitespace-pre-wrap">{claim.additionalInfo}</p>
        </Section>
      )}

      {/* Counterparty Response Section */}
      <ResponseSection>
        <SectionTitle>
          <FontAwesomeIcon icon={faReply} className="text-blue-500" />
          Counterparty Response
          <ResponseStatusBadge reviewed={claim.counterpartyReviewed}>
            {claim.counterpartyReviewed ? 'Responded' : 'Awaiting Response'}
          </ResponseStatusBadge>
        </SectionTitle>

        {/* Response Deadline */}
        {claim.responseDeadline &&
          !claim.counterpartyReviewed &&
          (() => {
            const timeRemaining = getTimeRemaining(claim.responseDeadline);
            return timeRemaining ? (
              <ResponseDeadlineBar isExpired={timeRemaining.isExpired}>
                <FontAwesomeIcon
                  icon={timeRemaining.isExpired ? faExclamationTriangle : faHourglass}
                />
                <span>{timeRemaining.text}</span>
              </ResponseDeadlineBar>
            ) : null;
          })()}

        {/* Response Details */}
        <InfoGrid>
          <InfoRow>
            <InfoLabel>Response Deadline</InfoLabel>
            <InfoValue>{formatDate(claim.responseDeadline)}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Counterparty Reviewed</InfoLabel>
            <InfoValue
              className={claim.counterpartyReviewed ? 'text-green-600' : 'text-yellow-600'}
            >
              {claim.counterpartyReviewed ? 'Yes' : 'No'}
            </InfoValue>
          </InfoRow>
        </InfoGrid>

        {/* Existing Response */}
        {claim.response && claim.counterpartyReviewed ? (
          <ExistingResponse className="mt-4">
            <ResponseHeader>
              <FontAwesomeIcon icon={faCheckCircle} className="text-green-500" />
              <ResponderName>
                {claim.responderId === claim.hostId ? 'Host' : 'Guest'} Response
              </ResponderName>
              <ResponseDate>on {formatDate(claim.response.createdAt)}</ResponseDate>
            </ResponseHeader>
            <ResponseContent>{claim.response.responseContent}</ResponseContent>
            {claim.response.responsePhotos && claim.response.responsePhotos.length > 0 && (
              <PhotoGrid style={{ marginTop: '1rem' }}>
                {claim.response.responsePhotos.map((p: string, i: number) => (
                  <PhotoWrapper key={`resp-${i}`}>
                    <Photo src={toPublicS3ObjectUrl(p)} alt={`Response ${i + 1}`} />
                  </PhotoWrapper>
                ))}
              </PhotoGrid>
            )}
          </ExistingResponse>
        ) : (
          <NoResponseYet className="mt-4">
            <FontAwesomeIcon icon={faHourglass} />
            <span>No response submitted yet</span>
          </NoResponseYet>
        )}
      </ResponseSection>

      {/* Admin Notes */}
      <Section>
        <SectionTitle>
          <FontAwesomeIcon icon={faClock} />
          Admin Notes
        </SectionTitle>
        {claim.adminNotes && (
          <AdminNotesSection>
            <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">
              {claim.adminNotes}
            </pre>
          </AdminNotesSection>
        )}
        <div className="mt-4">
          <ModalLabel>Add Note</ModalLabel>
          <AdminNotesTextarea
            value={newNote}
            onChange={e => setNewNote(e.target.value)}
            placeholder="Add a note to this claim..."
          />
          <Button
            text="Add Note"
            onClick={handleAddNote}
            isLoading={addingNotes}
            buttonWidth="120px"
          />
        </div>
      </Section>

      {/* Approve Modal */}
      <Modal
        isOpen={isApproveModalOpen}
        onClose={() => setIsApproveModalOpen(false)}
        title="Approve Claim"
        modalWidth="600px"
        modalHeight="auto"
      >
        <ModalContent>
          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <p className="text-sm text-blue-800 font-medium mb-2">Claim Amount Summary</p>
            <p className="text-sm text-blue-700">
              Original claim: ${claim.claimAmount?.toFixed(2) || 'N/A'}
            </p>
            {claim.protectionPlanSnapshot && (
              <p className="text-sm text-blue-700 mt-1">
                Guest Protection: {claim.protectionPlanSnapshot.planName} (Out of pocket: $
                {claim.protectionPlanSnapshot.outOfPocket})
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <ModalLabel>1. Approved Amount ($) *</ModalLabel>
              <NumericInput
                value={approvedAmount ? parseFloat(approvedAmount) : null}
                onValueChange={(value: number | null) => setApprovedAmount(value?.toString() ?? '')}
                placeholder="Total approved"
                minValue={0}
                width="100%"
              />
              <p className="text-xs text-gray-500 mt-1">Total damage appraisal</p>
            </div>
            <div>
              <ModalLabel>2. Host Claim Amount ($)</ModalLabel>
              <NumericInput
                value={hostClaimAmount ? parseFloat(hostClaimAmount) : null}
                onValueChange={(value: number | null) =>
                  setHostClaimAmount(value?.toString() ?? '')
                }
                placeholder="Platform pays host"
                minValue={0}
                width="100%"
              />
              <p className="text-xs text-gray-500 mt-1">What platform pays host</p>
            </div>
            <div>
              <ModalLabel>3. Guest Should Pay ($)</ModalLabel>
              <NumericInput
                value={guestShouldPay ? parseFloat(guestShouldPay) : null}
                onValueChange={(value: number | null) => setGuestShouldPay(value?.toString() ?? '')}
                placeholder="Guest owes"
                minValue={0}
                width="100%"
                allowDecimal={true}
              />
              <p className="text-xs text-gray-500 mt-1">Based on the guest's protection plan</p>
            </div>
          </div>

          <div className="mt-4">
            <ModalLabel>Admin Notes (optional)</ModalLabel>
            <ModalTextarea
              value={adminNotes}
              onChange={e => setAdminNotes(e.target.value)}
              placeholder="Add notes about this approval..."
            />
          </div>
          <div className="flex gap-4 mt-4">
            <Button
              text="Approve Full"
              onClick={() => handleApprove(false)}
              isLoading={updating}
              buttonWidth="150px"
            />
            <Button
              text="Partial Approve"
              theme="outlined"
              onClick={() => handleApprove(true)}
              isLoading={updating}
              buttonWidth="150px"
            />
          </div>
        </ModalContent>
      </Modal>

      {/* Reject Modal */}
      <Modal
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        title="Reject Claim"
        modalWidth="500px"
        modalHeight="auto"
      >
        <ModalContent>
          <div>
            <ModalLabel>Rejection Reason *</ModalLabel>
            <ModalTextarea
              value={rejectionReason}
              onChange={e => setRejectionReason(e.target.value)}
              placeholder="Explain why this claim is being rejected..."
            />
          </div>
          <div>
            <ModalLabel>Admin Notes (optional)</ModalLabel>
            <ModalTextarea
              value={adminNotes}
              onChange={e => setAdminNotes(e.target.value)}
              placeholder="Add internal notes..."
            />
          </div>
          <div className="flex gap-4 mt-4">
            <Button
              text="Cancel"
              theme="outlined"
              onClick={() => setIsRejectModalOpen(false)}
              buttonWidth="120px"
            />
            <Button
              text="Reject Claim"
              onClick={handleReject}
              isLoading={updating}
              buttonWidth="150px"
            />
          </div>
        </ModalContent>
      </Modal>
    </PageContainer>
  );
};

export default AdminClaimDetailPage;
